/**
 * `AW1` · La configuración de la instancia, leída EN TIEMPO DE EJECUCIÓN.
 *
 * ## Por qué no es una variable de build (`import.meta.env.VITE_…`)
 *
 * `apps/management-ui` es **un artefacto estático y UNA SOLA BUILD para todas
 * las instancias** —`docs/_reports/transversal/arquitectura.html` §1: «SPA de Refine en S3 +
 * CloudFront. No hay proceso que operar: es un bucket»—. Una variable `VITE_*`
 * se hornea en el bundle en tiempo de compilación, así que cada instancia
 * necesitaría su propia build: exactamente lo que la arquitectura dice que esto
 * no es. Es la misma decisión que `AW2` lleva con `guard` para las extensiones
 * aceptadas y el tamaño máximo, y vale igual para la dirección de `admin-web`.
 *
 * Entonces el bundle no lleva NINGUNA dirección adentro: la pide al arrancar,
 * al lado suyo en el bucket. El artefacto es idéntico en todas las instancias y
 * lo único que cambia es un archivo de texto que se sube junto a él.
 *
 * ## Y por qué esto no viola «la UI no guarda nada»
 *
 * `arquitectura.html` §1 dice de esta unidad: «Guarda · Nada. Ni credenciales ni
 * estado». Esto se lee y se mantiene en memoria mientras la pestaña vive; no se
 * escribe en `localStorage`, ni en `sessionStorage`, ni en una cookie. Y el
 * archivo NO puede llevar credenciales: es público —lo sirve el mismo
 * CloudFront que sirve el `index.html`, cualquiera lo puede pedir—. Lo único
 * que lleva es a dónde hablar y cada cuánto preguntar.
 */

import { mensajeDeError } from "./mensaje-de-error.ts"

/**
 * ⟦rastro · `R3-UI4` · A10 · [28/9/2026]⟧ ⛔⛔ **ACÁ VIVÍA `export interface
 * Enrolado` Y EL CAMPO `ConfiguracionInstancia.enrolados`, Y `R3-UI4` LOS SACA.** ⟦rastro · A10 · R3-UI4⟧
 *
 * ── LO QUE ACÁ HABÍA, TEXTUAL (no se borra: `ADR-0082`) ────────────────────
 *
 *   ⟦rastro · ~~`AW26` · Un participante que el motor RECONOCE, tal como el
 *   ⟦rastro · entrypoint lo derivó de `F7_PARTICIPANTS` (`brazo:id:direccion`,
 *   ⟦rastro · medido el 28/8/2026)~~: tres campos **sin traducir** con su rol —
 *   ⟦rastro · `brazo` (dato, no selector: lo deriva `bot-web` del canal),
 *   ⟦rastro · `id` (`ADR-0232`: los `uuid` de `F7_PARTICIPANTS` SON ids de
 *   ⟦rastro · `participants.participants`) y `direccion` (lo único que viaja
 *   ⟦rastro · como `de`).
 *   ⟦rastro · ~~`export interface Enrolado { brazo, id, direccion }`~~ y
 *   ⟦rastro · ~~`readonly enrolados?: readonly Enrolado[]`~~, poblado por el
 *   ⟦rastro · parser `enroladosDeclarados(crudo["enrolados"])`.
 *
 * ⭐ **POR QUÉ SALE, Y POR QUÉ NO ROMPE NADA**: el padrón dejó de declararse por
 * configuración —qué participantes reconoce el motor sale del módulo, no del
 * archivo de la instancia—, así que `enrolados` se va del campo, del parser, del ⟦rastro · A10 · R3-UI4⟧
 * spread y de TODA la prosa. `interpretarConfiguracion` ya **descarta claves
 * desconocidas**, así que una instancia que todavía traiga un `enrolados` viejo ⟦rastro · A10 · R3-UI4⟧
 * en su archivo arranca igual. Lo verifica `R3-UI4` con el chequeo
 * `enrolados-fuera`. ⚠️ Cualquier línea de este archivo que vuelva a nombrar el ⟦rastro · A10 · R3-UI4⟧
 * token tiene que llevar `⟦rastro` o el chequeo se pone rojo.
 */

/**
 * Lo que la instancia configura para la pantalla.
 *
 * Deliberadamente chico: cada campo que se agregue acá es un campo que el
 * operador de cada instancia tiene que llenar bien para que la pantalla ande.
 */
export interface ConfiguracionInstancia {
  /**
   * La raíz HTTP de `admin-web`.
   *
   * ⛔⭐ **ACÁ DECÍA «el ÚNICO con el que esta unidad habla», y DEJÓ DE SER
   * CIERTO el 26/8/2026.** `ADR-0197` (`accepted`, `module: "management-ui"`)
   * decidió, textual: *«`apps/management-ui` le habla DIRECTO a `apps/bot-web`,
   * por HTTP, con CORS de orígenes explícitos y nunca `*`»* — y enumeró los
   * cuatro sitios vivos donde el invariante estaba escrito **sin editar ninguno**,
   * porque ninguno era su territorio. Éste es uno de los dos que sí son
   * territorio de `AW16`, y por eso se corrige acá.
   *
   * ⚠️ **Se REDACTA y no se borra** (`ADR-0082`): la frase era cierta cuando se
   * escribió, y un lector que llegue con el invariante viejo en la cabeza tiene
   * que encontrar acá qué lo levantó y por qué. El motivo de `ADR-0197` es de
   * perímetro y no de comodidad: *«`admin-web` es el proceso del plano HUMANO
   * —ahí vive Better Auth + MFA—; `bot-web` es el del plano PARTICIPANTE, con
   * cero credenciales humanas. Un proxy junta los dos planos en un proceso, y el
   * que los junta es justamente el que tiene las credenciales.»*
   *
   * ⇒ **hoy esta unidad habla con DOS procesos**: con `admin-web` siempre, y con
   * `bot-web` **sólo si la instancia declaró `botWebUrl`** (ver abajo). Los otros
   * dos sitios del invariante —`specs/F1-banco-de-contenido/etapas/pantalla.md:111`
   * y `docs/_reports/transversal/arquitectura.html:139`— **no son territorio de
   * esta etapa y siguen diciendo lo viejo. Dueño: TL.**
   *
   * No tiene default a propósito. Un default —`localhost`, u origen propio—
   * haría que una instancia mal configurada arrancara igual y fallara recién al
   * pedir datos, con un error de red que no dice que falta configurar nada.
   */
  readonly adminWebUrl: string

  /**
   * ⭐ `AW16` · **La raíz HTTP de `apps/bot-web`. OPCIONAL, y SIN default.**
   *
   * Es la dirección del proceso del plano PARTICIPANTE, y lo único que la usa es
   * la pantalla del simulador de chat (`src/simulador/`). `ADR-0197` §«Lo que NO
   * decide» fijó la regla que este campo hereda, textual: *«No decide cómo la
   * pantalla configura la segunda dirección. Hoy `ConfiguracionInstancia` tiene
   * una sola URL; esto le agrega una. **Y hereda su regla: sin default.**»*
   *
   * ⛔⛔ **AUSENTE ⇒ LA PANTALLA DEL SIMULADOR NO SE MONTA Y SU ENTRADA DE
   * NAVEGACIÓN NO APARECE.** No se monta con un cartel de error, no se monta
   * apuntando a `localhost`, y no se monta rota. `ADR-0196` puso el simulador
   * contra la **base local de desarrollo**, así que **en una instancia desplegada
   * su ausencia es lo correcto**, no un defecto de configuración.
   *
   * ⛔ **Las dos salidas cómodas, y la segunda es la que hace daño:**
   *
   *   ① **un default a `http://127.0.0.1:8788`** —el puerto real,
   *      `apps/bot-web/src/entorno.ts` `PUERTO_DEFAULT`—, que haría que «ande sin
   *      configurar nada» en la máquina de quien lo escribe. Es exactamente el
   *      error que el campo de arriba ya pagó del otro lado y dejó escrito;
   *   ② **montar la pantalla igual y mostrar un error adentro.** Suena más amable
   *      y es peor: pone una pantalla de simulador en el Management Tool de una
   *      instancia desplegada, donde `ADR-0197` §Costo ② mide que el pedido **ni
   *      siquiera llega** —`bot-web` escucha en `127.0.0.1`—. El operador ve una
   *      pantalla rota, la reporta como bug, y alguien la «arregla» poniendo
   *      `BOT_WEB_HOSTNAME=0.0.0.0`, que es **desarmar la más fuerte de las cuatro
   *      compuertas** de un proceso que no autentica a nadie (`ADR-0198` §④).
   *
   * ⚠️⛔ **Y ESTA COMPUERTA ES COSMÉTICA, QUE ES LO QUE HAY QUE SABER DE ELLA.**
   * `ADR-0198` la tabula como la compuerta **①**, la más débil de cuatro: lo único
   * que hace es que el bundle no emita pedidos. **Las que protegen de verdad son
   * el flag del entorno de `bot-web` (② y ③, la lista de orígenes CORS que falla
   * cerrada) y el loopback (④, `HOST_DEFAULT = "127.0.0.1"`, que no depende de que
   * nadie configure nada bien).** Este campo no protege `bot-web` de nadie: sacarlo
   * de un `configuracion.json` no le cierra una sola puerta al proceso.
   */
  readonly botWebUrl?: string

  /**
   * ⭐⭐ `R2-UI6` · **LA RAÍZ HTTP DE `auth.<sitio>` — `apps/identity-web`.
   * OBLIGATORIA, Y SIN DEFAULT.** `// ADR-0353`
   *
   * Es a quién le pregunta la puerta: `sign-in`, `sign-out`, `get-session` y
   * `elegir-contrasena` (`src/login/borde-sesion.ts`). `R2-UI5` dejó escrito que
   * *«la raíz de `auth.<sitio>` … entra por props, y es `R2-UI6` quien la
   * resuelve»* (`src/login/index.ts`); acá se resuelve.
   *
   * ⛔ **`[CONFIG]` de instancia y NO `VITE_*`**, por la misma razón que
   * `adminWebUrl`, escrita arriba de este archivo: el bundle es UNO y se publica
   * a todas las instancias, así que una dirección horneada en tiempo de compilado
   * obligaría a un artefacto por instancia.
   *
   * ⛔⛔ **OBLIGATORIA, y no opcional, y la diferencia es de modo de falla.**
   * `botWebUrl` es opcional porque su ausencia tiene un significado válido —el
   * simulador no se monta—. La sesión no tiene esa salida: **sin esta dirección
   * no hay puerta**, y un default —`localhost`, u origen propio— haría que una
   * instancia mal configurada arrancara igual y mandara las contraseñas de los
   * operadores a un host equivocado. Ausente ⇒ `interpretarConfiguracion` tira
   * con un mensaje que nombra el campo, que es la misma disciplina de
   * `adminWebUrl`.
   *
   * ⚠️⛔ **EL RADIO QUE ESTO TIENE FUERA DE `src/`, DECLARADO Y NO TAPADO.**
   * Volverla obligatoria pone rojo a **todo `configuracion.json` que no la
   * declare**, y esos archivos NO son territorio de esta etapa:
   * `apps/management-ui/configuracion.example.json`, el `README.md` de esta app,
   * el `configuracion.json` del UAT y las tres evidencias que cuelgan de
   * `qa/_ejecucion-R1/casos/`. **Quedan reportadas en el informe de
   * `R2-UI6` con dueño, sin editarlas.** Falla ruidosa y con el campo nombrado,
   * que es lo contrario de una pantalla en blanco.
   */
  readonly identityWebUrl: string

  /**
   * ⭐⭐ `AW59` · **LA ESPERA MÁXIMA DEL SIMULADOR. OPCIONAL, Y SIN DEFAULT.**
   *
   * Es el techo del indicador de turno en curso: **cuántos milisegundos como
   * máximo la pantalla del simulador sigue mostrando un turno como en curso sin
   * haber visto ninguna respuesta nueva.** Lo único que lo usa es
   * `src/simulador/chat.tsx`.
   *
   * ⛔⛔ **AUSENTE ⇒ NO SE DIBUJA NADA.** Ni el indicador, ni el aviso de que se
   * pasó la espera, ni un cartel de que falta configurar algo. **No es un error de
   * configuración: es una función apagada**, y la pantalla queda idéntica a la de
   * antes de `AW59`. Fundamento: `ADR-0040 §④`, *«sin configuración, el
   * comportamiento es idéntico al de hoy»* — que es la misma regla con la que el
   * propio `turn-worker` trata a su aviso de espera, y el mismo molde que
   * `botWebUrl` ya sostiene doce renglones más arriba.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * ⛔⛔ CONTRA QUÉ SE FIJA ESTE NÚMERO — Y NADIE LO VA A HACER CUMPLIR
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * Se pone en **el tope duro del turno de ESTA instancia, o por debajo; NUNCA por
   * encima.** Ese tope vive en **otro proceso**: es la variable de entorno
   * `TURN_WORKER_TURN_DEADLINE_MS` de `apps/turn-worker`.
   *
   * ⭐ **Por qué ése y no otro número:** es el único después del cual «no viene
   * nada» es **verdad**. El aviso de espera del worker tiene su umbral **por
   * construcción menor** que el tope —el arranque del worker lo hace cumplir—, así
   * que el tope cubre los dos mundos: la instancia que tiene aviso de espera y la
   * que no.
   *
   * ⛔⛔ **Y POR ESO NO PUEDE LLEVAR DEFAULT, Y EL ARGUMENTO ES ARITMÉTICO.** El
   * tope del otro proceso es configurable en un rango de tres órdenes de magnitud y
   * **esta unidad no lo puede leer** —son dos procesos, y la única dependencia que
   * el SPA declara es hacia los contratos—. ⇒ **cualquier default que se eligiera
   * quedaría por encima del tope de alguna instancia**, y estar por encima es
   * exactamente el caso que `ADR-0040 §②` prohíbe: el indicador seguiría girando
   * sobre un turno que ya murió, **prometiendo una respuesta que no va a llegar**.
   *
   * ⭐ **La asimetría que decide, y hay que verla entera:**
   *
   *   · un techo **más corto** que el tope real apaga el indicador antes de tiempo
   *     ⇒ la pantalla **deja de afirmar algo**, y eso está permitido;
   *   · un techo **más largo** deja al indicador **prometiendo** ⇒ prohibido.
   *
   * ⇒ **ante la duda se acorta; ante la ausencia no se dibuja.**
   *
   * ⚠️⚠️ **ESTO ES UNA PROSA SIN QUIEN LA HAGA CUMPLIR, Y VA DICHO.** Son **dos
   * números en dos procesos con dos entornos distintos**, y **ningún instrumento de
   * este repo compara uno con el otro**: ni `tsc` —no hay import posible—, ni los
   * jueces, ni ninguna de las tres suites. **El día que alguien suba el tope del
   * worker y no toque esto, nada va a avisar**, y el síntoma sería un indicador que
   * se apaga antes de tiempo (el lado permitido). El día que lo BAJE y no toque
   * esto, el síntoma es el lado prohibido y tampoco avisa nada. **Residual
   * declarado con dueño (TL).**
   *
   * ⚠️ **No es «cada cuánto se pregunta» y no se deriva de `refrescoMs`.** El
   * refresco es el intervalo del sondeo —el de una tabla, no el de un chat— y es
   * configurable por instancia: atarle el vencimiento haría que el techo del chat
   * cambiara solo cuando un operador toca una cosa que no tiene nada que ver.
   * ⚠️ **Y la granularidad real del apagado ES `refrescoMs`**, porque la respuesta
   * sólo se conoce cuando el sondeo la trae: un techo declarado acá se puede notar
   * un intervalo más tarde. Eso no se arregla acortando el refresco.
   */
  readonly esperaMaximaDelSimuladorMs?: number

  /**
   * Cada cuántos milisegundos la pantalla vuelve a PREGUNTAR por la tabla.
   *
   * Es el `guard` del refresco: la pantalla pregunta, nadie le empuja. El valor
   * es de instancia porque el costo de preguntar lo paga `admin-web`, y cuántos
   * operadores hay mirando la tabla a la vez es un dato de operación.
   */
  readonly refrescoMs: number

  /** Cuántas filas pide por página. Se manda al borde, no se recorta acá. */
  readonly pageSize: number

  /**
   * ⭐ `CP-F4-010` · **Cuál de los brazos declarados es el HÍBRIDO.** Opcional.
   *
   * Existe por un defecto de criterio inequívoco: «Pidió humano» se dibujaba en
   * el brazo `puro`, y el PRD dice *«Pidió humano **(solo Híbrido)**»*
   * (`docs/prd/F4.md:45`, `F8.md:60`). El botón «pásame con una persona» **no
   * existe en el brazo puro**, así que la columna ahí no es un cero: es una
   * columna que no tiene sentido.
   *
   * ⚠️ **Por qué es CONFIGURACIÓN y no una constante, y no es una preferencia:**
   * `contracts/platform/index.ts:138` declara `type Arm = string`, y
   * `contracts/exports/index.ts:296-302` explica que los brazos son `[CONFIG]`
   * por instancia (`annex-a-config.md` §Brazos e instancia) — *«una constante
   * `"Híbrido"` o un `type Arm = "puro" | "hibrido"` haría que la instancia que
   * declara otros nombres arranque con un contrato que no la describe»*. El
   * mismo contrato deja escrito que cuál brazo es el híbrido *«es hoy una regla
   * de la PANTALLA, no del tipo»*. Ésta es esa pantalla, y ésta es esa regla.
   *
   * ⛔ **Ausente ⇒ no se oculta nada, y la dirección del default importa.** Con
   * un default `"hibrido"`, una instancia que llame a sus brazos `A` y `B`
   * escondería la columna en LOS DOS —una métrica que desaparece sin que nadie
   * lo note—. Sin default, lo peor que pasa es que se siga viendo de más, que es
   * exactamente el comportamiento de hoy y se ve a simple vista. **Se prefiere
   * fallar hacia mostrar de más antes que hacia esconder en silencio.**
   *
   * ⇒ Para que `CP-F4-010` quede cerrado, la instancia tiene que declarar esto
   * en su `configuracion.json`. Está en `configuracion.example.json` y en el
   * informe.
   */
  readonly brazoHibrido?: string

  /**
   * ⭐⭐ `[22/9/2026]` · **El nombre visible de ESTA instancia.**
   *
   * ⛔⛔ **HASTA HOY DECÍA `KAI`, HORNEADO EN `zonas.ts:45`**, con este descargo
   * escrito ahí: *«No sale de `configuracion.json` porque los criterios la
   * nombran por su nombre —“KAI · Brazo Puro”— y porque `banco-de-contenido`
   * YA la escribe a mano»*. **Lo levanta un hecho medido**: se desplegó `OLI`, y
   * la consola de `OLI` se rotulaba `KAI` en cada sello, cada miga y el `<h1>` de
   * Inicio.
   *
   * ⭐ Es el **mismo molde exacto** que `canales`, `brazos` y `brazoHibrido`: un
   * dato que vive en el `.env` de la instancia y que el navegador no puede
   * derivar, así que lo emite el entrypoint
   * (`infra/docker/management-ui/30-configuracion.sh`) **derivándolo del
   * `nombre` de la variable `INSTANCIA`** —la declaración que `PRD_R1.md` §4
   * define y que `V1` obliga a traer `nombre` no vacío—. Derivarlo, en vez de
   * pedir una variable aparte, es `ADR-0231` otra vez: *«dos listas que tienen
   * que coincidir y se escriben en dos lugares DIVERGEN»*.
   *
   * ⛔ **Ausente es legítimo y NO se le inventa un default.** Sin nombre, el
   * sello es sólo el del brazo («Brazo Puro») y el rótulo de instancia no se
   * dibuja: **no se muestra lo que no se declaró**. Un default `"KAI"` acá sería
   * literalmente el defecto que este campo repara. Ver `zonas.ts`
   * ⟦la-declaracion-se-instala-antes-de-montar⟧.
   *
   * ⚠️ Un `""` o un valor que no sea texto se RECHAZA, igual que `brazoHibrido`:
   * «no lo declaró» y «lo declaró mal» tienen que poder distinguirse.
   */
  readonly instancia?: string

  /**
   * ⭐⭐ `[2/9/2026]` · **Los brazos que esta instancia declaró, en su orden.**
   *
   * Existe porque el dueño del producto lo pidió probando, textual: *«las
   * plantillas deberían mostrarse todas, independientemente de si es puro o
   * híbrido, en cambio tener un filtro o selector […] y no depender del path de
   * la url para verlas»*.
   *
   * ⛔⛔ **HASTA HOY ESTA APP NO PODÍA SABER QUÉ BRAZOS EXISTEN, Y ESO ERA
   * DELIBERADO.** `App.tsx:85-100` lo dice entero: `contracts/platform:138` fija
   * `type Arm = string` y los brazos son `[CONFIG]` por instancia
   * (`annex-a-config.md` §Brazos e instancia), así que un
   * `type Arm = "puro" | "hibrido"` **«haría que la instancia que declara otros
   * nombres arranque con un contrato que no la describe»**. Toda pantalla trata
   * el brazo como el string opaco que el contrato dice que es, y lo saca de la
   * RUTA (`ADR-0136` ②).
   *
   * ⭐ **Este campo NO afloja nada de eso, y por eso entra por acá y no por un
   * literal ni por un `type`.** Sigue siendo `[CONFIG]` por instancia, sigue
   * siendo una lista de strings opacos, y quien despliega la escribe. Es el
   * **mismo molde exacto que `canales` y que `brazoHibrido`**, que ya resolvieron
   * este problema —un dato que vive en el `.env` de otro proceso y que el
   * navegador no puede derivar— y **cuyo precedente ya está pagado**.
   *
   * ⚠️⛔ **AUSENTE, VACÍA, O CON UN SOLO BRAZO ⇒ NO SE DIBUJA NINGÚN SELECTOR**, y
   * la pantalla de plantillas se comporta **exactamente como antes**: un brazo, el
   * de la ruta. Mismo criterio que `botWebUrl`, `canales` y `enrolados`: no se ⟦rastro · A10
   * dibuja un `<select>` vacío, no se inventa un brazo, y **ausente es un valor
   * legítimo que va a ocurrir de verdad** —hay instancias de un solo brazo—.
   *
   * ⛔ **Esta unidad NO puede derivarlos, y nada verifica que coincidan con los
   * de verdad.** Los brazos reales los conocen el `.env` de `bot-web` (por el
   * canal receptor) y la política de aislamiento de la base; **ninguno de los dos
   * viaja por un cable que el navegador alcance**. Que esta lista diga la verdad
   * es responsabilidad de quien despliega. **Hueco declarado, dueño
   * `apps-integrator`** — el mismo dueño y el mismo hueco que `canales` ya tiene
   * escrito doce renglones más abajo, y por el mismo motivo.
   *
   * ⚠️ **Y una lista que MIENTA no es inocua**: un brazo de más pide un catálogo
   * que la política de aislamiento va a tapar, y la pantalla lo va a mostrar como
   * un brazo que falló. Es visible y ruidoso —que es el lado correcto por el que
   * fallar—, pero **no es gratis**, y queda dicho acá.
   */
  readonly brazos?: readonly string[]

  /**
   * ⭐⭐⭐ `ui-marco` · `[23/9/2026]` · **QUÉ BRAZOS PUEDEN DERIVAR A UNA PERSONA.**
   *
   * ════════════════════════════════════════════════════════════════════════
   * ⛔⛔ QUIÉN NECESITA ESTO, Y POR QUÉ NO PODÍA SALIR DE NINGÚN LADO
   * ════════════════════════════════════════════════════════════════════════
   *
   * La pantalla de «Botones de respuestas automáticas» (`R2-UI8`) ofrece
   * **3 ó 4 opciones en “Además de responder” según la derivación esté habilitada
   * o no** — regla 15 (`PRD_R2.md:1247`): *«“Pidió humano” nunca se registra en el
   * Brazo Puro»*, y `PRD_R2.md:1209`: *«No existe, ni en pantalla ni por pedido al
   * servidor»*.
   *
   * ⛔⛔ **Medido el 23/9/2026: `derivacionHabilitada` tenía CERO apariciones en
   * `apps/management-ui/src`.** El dato no estaba, y la única decisión por
   * capacidad que la SPA tomaba era `padron/tabla.tsx` (`columnasDelBrazo`)
   * **comparando contra el literal `"hibrido"`** — que `gates/literales-de-brazo.spec.ts`
   * ya acusa (medido hoy: ese gate está en **5 pass · 1 fail**, con ese archivo
   * entre los diez hallazgos). ⇒ copiar aquella forma habría agrandado un rojo que
   * ya existe.
   *
   * ════════════════════════════════════════════════════════════════════════
   * ⭐ DE DÓNDE SALE EL DATO · NO SE INVENTA UNA FUENTE, SE CABLEA LA QUE HAY
   * ════════════════════════════════════════════════════════════════════════
   *
   * `contracts/admin-api/index.ts:3017-3021` lo dice con todas las letras, sobre
   * `AutoRepliesAdminApiDeps.derivacionHabilitadaDe`: *«Lo que recibe es la
   * CAPACIDAD, no el nombre: la derivación ya está declarada por brazo en
   * `INSTANCIA.brazos[].derivacionHabilitada` —`V4` fija exactamente un brazo con
   * derivación— y `apps/admin-web/src/brazo-compartido.ts:288`
   * (`derivacionHabilitadaDe(`) ya la resuelve hoy `[medido 21/9/2026]`. **Se
   * recibe, no se deduce.**»*
   *
   * Y `contracts/channel/index.ts:2645-2655` enumera las tres superficies que ya
   * la reciben —la declaración de instancia, `contracts/admin-api`, y
   * `contracts/agent` como `["derivacionHabilitada", "booleano"]`
   * (`index.spec.ts:577`)— y cierra: *«es el MISMO dato, no uno nuevo»*.
   * `ADR-0341` §Actualización la agregó además a `ConfigDelEntrante`.
   *
   * ⇒ **ésta es la CUARTA superficie del mismo dato, y entra igual que las otras
   * tres: recibida.** ⛔ **Nunca deducida de un nombre de brazo.**
   *
   * ════════════════════════════════════════════════════════════════════════
   * ⛔ POR QUÉ POR `[CONFIG]` Y NO POR EL CABLE DE `admin-web`
   * ════════════════════════════════════════════════════════════════════════
   *
   * **Mismo mecanismo y mismo argumento que `canales` (`AW23`, `ADR-0231`) y que
   * `brazos` (`ADR-0249`), y no es analogía: es el mismo problema.** La
   * declaración de instancia vive en el `.env` del proceso del motor y **no viaja
   * por ningún cable que el navegador alcance**; el compose le da a `management-ui`
   * el MISMO `.env` que a `bot-web` (`compose.pruebas.yml:181`), así que el
   * entrypoint puede derivarla y las dos lecturas coinciden **por construcción**.
   * Es exactamente lo que `infra/docker/management-ui/30-configuracion.sh` ya hace
   * con `F7_CHANNELS` → `canales` y con `STUDY_ARMS` → `brazos`.
   *
   * ⚠️⛔ **EL HUECO, DECLARADO IGUAL QUE EL DE SUS DOS HERMANAS Y CON EL MISMO
   * DUEÑO.** Esta unidad NO puede derivar la capacidad sola, y **nada verifica que
   * esta tabla coincida con la declaración de instancia de verdad**: que coincidan
   * es responsabilidad de quien despliega. **Hueco declarado, dueño
   * `apps-integrator`.**
   *
   * ⛔⛔ **Y hay un segundo hueco, que es NUEVO y no lo tienen `canales` ni
   * `brazos`: el bloque que lo derive en `infra/docker/management-ui/30-configuracion.sh`
   * NO ESTÁ ESCRITO.** Ese archivo **vive fuera del territorio de
   * `apps/management-ui/**`** y su propio comentario (`:60-64`, `:124-127`) dice que
   * esto YA PASÓ DOS VECES —con `canales` y con `brazos`—: *«Nadie era dueño de
   * esta línea, y el reparto por territorio la fabricó»*. ⇒ **hasta que ese bloque
   * exista, una instancia desplegada va a arrancar SIN este campo**, con el local
   * andando. Queda dicho antes y no después. **Dueño: `apps-integrator`.**
   *
   * ════════════════════════════════════════════════════════════════════════
   * LA FORMA · por qué un MAPA y no una lista de brazos que derivan
   * ════════════════════════════════════════════════════════════════════════
   *
   * ⛔⛔ **Una lista (`brazosConDerivacion: ["hibrido"]`) colapsa «este brazo
   * declaró `false`» con «nadie declaró nada», y ésa es justo la distinción que no
   * se puede perder.** `brazo-compartido.ts:265-285` lo argumenta entero para el
   * servidor, y el argumento es el mismo acá: leer un ausente como `false` **afirma
   * que ese brazo no deriva** —esconde una opción que sí existe—, y leerlo como
   * `true` ofrece registrar «pidió humano» en el Brazo Puro, que es el defecto que
   * todo este frente existe para cerrar. Con un mapa, una clave ausente es
   * distinguible de una clave en `false`.
   *
   * ⛔ **Las CLAVES son nombres de brazo `[CONFIG]`, escritas por quien despliega**
   * —nunca un literal en el código, que es lo que `gates/literales-de-brazo.spec.ts`
   * prohíbe—. `type Arm = string` (`contracts/platform`), así que esta capa **no
   * puede validarlas** contra nada; ver el hueco de arriba.
   *
   * ⚠️⛔ **AUSENTE ES UN VALOR LEGÍTIMO Y VA A OCURRIR DE VERDAD** —hoy ocurre en
   * TODA instancia, porque el bloque del entrypoint no existe—. Mismo molde que
   * `botWebUrl`, `canales` y `enrolados`. **Qué dibuja la pantalla cuando la ⟦rastro · A10
   * capacidad no se puede determinar es decisión de `R2-UI8`** (`src/plantillas/**`,
   * otro territorio); lo que esta capa garantiza es que pueda PREGUNTARLO sin
   * elegir un default por accidente: ver `derivacionHabilitadaDe`, que devuelve
   * `null` y no un booleano.
   *
   * ⛔ **Y un valor presente pero inservible se RECHAZA en vez de degradarse a
   * ausente**, igual que `botWebUrl` y `canales`: `"true"` (la cadena) o un `1` no
   * se coercionan. Los dos estados —«no lo declaró» y «lo declaró mal»— tienen que
   * poder distinguirse.
   */
  readonly derivacionPorBrazo?: Readonly<Record<string, boolean>>

  /**
   * ⛔⛔⛔ **DOCBLOCK HUÉRFANO · anotado el 23/9/2026 por `ui-marco`, y NO se borra
   * (`ADR-0082`).**
   *
   * **① No declara ningún campo.** Se cierra, y lo que sigue es OTRO docblock
   * —el de `canales`—, así que este comentario no documenta nada: los tres campos
   * que describe son `botonInicio`, `botonDerivacion` y `botonBaja`, que viven
   * ~90 renglones más abajo **con una sola línea de comentario cada uno**. Quedó
   * así por una edición anterior; el efecto es que quien lea los campos no
   * encuentra el argumento, y quien lea el argumento no encuentra los campos.
   *
   * **② Y lo que afirma del contrato YA NO ES CIERTO.** Medido hoy sobre el árbol:
   * `grep -n "readonly optOutButtonId\|readonly demoStartButtonId\|readonly
   * demoHandoffButtonId" contracts/channel/index.ts` da **CERO**. `R2-CCH2` retiró
   * SIETE campos de `ChannelConfig` —`demoModeEnabled`, `demoStartCopy`,
   * `demoStartButtonId`, `demoHandoffCopy`, `demoHandoffButtonId`, `optOutCopy` y
   * `optOutButtonId`— por `RT-6` (`PRD_R2.md:1262`: *«no queda ninguna lectura de
   * configuración del servidor que decida qué contesta un botón»*), y lo dejó
   * escrito en `contracts/channel/index.ts:2338-2339` y en
   * `index.spec.ts:1100-1101`. ⇒ **las tres citas de línea de acá abajo apuntan a
   * campos que ya no existen.**
   *
   * ⚠️ **Lo que NO se resolvió, y por eso queda anotado en vez de reescrito:** si
   * los tres campos de la SPA siguen teniendo sentido sin esos tres del contrato
   * es una pregunta del simulador (`src/simulador/**`), que es territorio de otra
   * etapa. Esta anotación deja el rastro; la decisión es de quien sea dueño de
   * esos campos. **No se midió** qué pasa hoy en el simulador con los tres ids.
   *
   * ── EL TEXTO ORIGINAL, INTACTO ─────────────────────────────────────────────
   *
   * ⭐ `AW20` · Los TRES identificadores de botón que el brazo de esta instancia
   * rutea de verdad, para que el simulador los DIBUJE en vez de hacerlos tipear.
   *
   * ⛔ **Son `[CONFIG]` por brazo y por eso entran por acá y no por un literal en
   * el componente.** `contracts/channel/index.ts` los declara así —`demoStartButtonId`
   * (`:1994`), `demoHandoffButtonId` (`:2033`) y `optOutButtonId` (`:2206`)— porque
   * **el título visible y el id los escribimos nosotros en la plantilla de cada
   * brazo**, así que dos brazos pueden tener ids distintos para el mismo botón.
   * Un literal horneado andaría en el brazo de quien lo escribió y caería en
   * `interactivo-sin-ruteo` en el otro, **sin que nada falle** — y ese síntoma es
   * indistinguible del hueco real de `CH8`, que es justo lo que la pantalla existe
   * para mostrar.
   *
   * ⛔ **Cualquier otro identificador cae en `interactivo-sin-ruteo`**, así que
   * dibujar un cuarto botón sería dibujar algo que no hace nada.
   *
   * **Opcionales, sin default, y ausente ⇒ ese botón NO se dibuja** — ni
   * deshabilitado, ni con un valor de relleno. Es la misma regla que `botWebUrl`
   * ya sostiene, y por el mismo motivo.
   */
  /**
   * ⭐⭐ `AW23` · Los canales receptores que esta instancia rutea, **en orden**.
   * El PRIMERO es el que el simulador elige por defecto.
   *
   * ⛔ **No pueden ser un literal en el componente.** Los nombres los mapea
   * `F7_CHANNELS` **en el entorno de `bot-web`** —otro proceso—, y quien despliega
   * elige cómo se llaman. Un `useState("canal-puro")` anda en el ambiente de quien
   * lo escribió y deja la otra instancia apuntando a un canal **que no existe**: el
   * motor contesta `canal-no-ruteable`, que es un desenlace legítimo del contrato y
   * **se lee como un defecto del ruteo**. El síntoma no dice «configuración».
   *
   * ⚠️ **Ausente o vacía ⇒ el simulador cae al `<input>` de texto libre.** No se
   * dibuja un `<select>` vacío y no se inventa un canal.
   *
   * ⛔ **Esta unidad NO puede derivarlos.** `F7_CHANNELS` no viaja por ningún cable
   * que la pantalla alcance; que las dos listas coincidan es responsabilidad de quien
   * despliega, y **nada lo verifica**. Hueco declarado, dueño `apps-integrator`.
   */
  readonly canales?: readonly string[]

  /**
   * ⭐⭐ `AW26` · **Los participantes que el MOTOR reconoce** — los ENROLADOS —,
   * para que el simulador pueda impersonar a alguien que no dé
   * `emisor-desconocido` por construcción.
   *
   * ⛔⛔ **NO ES EL PADRÓN, Y ÉSA ES LA DECISIÓN ENTERA.** `ADR-0232` midió que hay
   * **dos registros de participantes** y que `AW18` leía el equivocado: el motor
   * resuelve al emisor contra una **lista blanca de `[CONFIG]`** (`F7_PARTICIPANTS`)
   * porque **`modules/participants` no existe** —`recibir.ts:823` hace
   * `deps.participants.find(arm, from)` contra un `Map` armado desde el `.env`
   * (`composicion.ts:615-616`)—, **no** contra la tabla `participants.participants`
   * que sirve `admin-web`. ⇒ elegir cualquier fila del buscador del padrón daba
   * `emisor-desconocido` **siempre, por construcción**.
   *
   * ⭐ **Se DERIVA en el entrypoint, con el mismo mecanismo y el mismo argumento que
   * `canales`** (`ADR-0231`): el compose le da a `management-ui` el MISMO `.env` que
   * a `bot-web` (`compose.pruebas.yml:181`), así que **las dos listas coinciden por
   * construcción y no por disciplina**. Esta unidad NO puede derivarlo sola —
   * `F7_PARTICIPANTS` no viaja por ningún cable que el navegador alcance—.
   *
   * ⛔ **Lo que viaja como `de` al `POST /entrada` es la `direccion`, nunca el `id`.**
   * `POST /entrada` simula el webhook de Meta, **donde el emisor ES una dirección**.
   * Mandar el `id` haría al simulador **menos fiel al canal real**, que es
   * exactamente lo que `ADR-0196` dice que el simulador existe para evitar: el día
   * que se conecte Meta de verdad, el camino probado no sería el que corre.
   *
   * ⛔ **El `brazo` de cada entrada es un DATO, no un selector.** `bot-web` deriva el
   * brazo del **canal receptor** y contesta `canal-no-ruteable` si no rutea. La
   * pantalla puede mostrarlo; no puede usarlo para elegir canal ni para filtrar la
   * lista de canales.
   *
   * ⚠️⛔ **AUSENTE ES UN VALOR LEGÍTIMO Y VA A OCURRIR DE VERDAD.** Mismo molde que
   * `botWebUrl` y `canales`: ausente ⇒ el selector de enrolados **no se dibuja** y ⟦rastro · A10
   * quedan los otros dos caminos (el buscador del padrón y la dirección a mano). **No
   * se dibuja un `<select>` vacío y no se inventa un participante.** La guarda de PII
   * del entrypoint **omite la clave entera** cuando alguna dirección tiene forma de
   * teléfono (`ADR-0232` §La guarda de PII, que falla CERRADO porque
   * `configuracion.json` se sirve sin autenticación), así que la ausencia es un
   * estado operativo y no una hipótesis.
   *
   * ⛔ **Y un valor presente pero inservible se RECHAZA en vez de degradarse a
   * ausente**, igual que `botWebUrl`: los dos estados —«la instancia no lo declaró» y
   * «la instancia lo declaró mal»— tienen que poder distinguirse.
   */
  readonly botonInicio?: string
  /** El de derivación a humano. Ver `botonInicio`. */
  readonly botonDerivacion?: string
  /** El de baja / opt-out. Ver `botonInicio`. */
  readonly botonBaja?: string
}

/**
 * Dónde se busca el archivo, RELATIVO al documento.
 *
 * Relativo y no absoluto para que el artefacto ande igual servido desde la raíz
 * del bucket o desde un subdirectorio, sin recompilar.
 */
const RUTA_CONFIGURACION = "configuracion.json"

/**
 * Los dos valores que sí tienen default, y por qué éstos y no `adminWebUrl`.
 *
 * Un `refrescoMs` o un `pageSize` ausentes tienen una respuesta razonable que
 * la pantalla puede dar sola y que no es silenciosamente incorrecta. Una
 * dirección de `admin-web` ausente no: cualquier valor que inventáramos apunta
 * a algún lado, y el lugar equivocado.
 *
 * 10 filas es el default de Refine y el orden de magnitud de una tabla que se
 * lee sin scrollear. 5 s es lo bastante seguido para que un documento que pasa
 * de `en proceso` a `indexado` se vea sin que el operador haga nada, y lo
 * bastante espaciado para que doce operadores mirando la tabla no sean doce
 * consultas por segundo contra `admin-web`.
 */
const REFRESCO_MS_POR_DEFECTO = 5_000
const PAGE_SIZE_POR_DEFECTO = 10

/** Falla de configuración, separada de una falla de red o del borde. */
export class ErrorDeConfiguracion extends Error {
  override readonly name = "ErrorDeConfiguracion"
}

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null && !Array.isArray(valor)
}

/**
 * Un entero positivo, o el default. Nunca un valor inservible en silencio.
 *
 * Un `refrescoMs: 0` o negativo apagaría el refresco sin decirlo —el criterio
 * de `P0-2` dejaría de cumplirse y la tabla se vería igual—, y un `pageSize: 0`
 * pediría páginas vacías para siempre. Los dos se rechazan fuerte en vez de
 * degradarse.
 */
function enteroPositivo(valor: unknown, campo: string, porDefecto: number): number {
  if (valor === undefined || valor === null) return porDefecto
  if (typeof valor !== "number" || !Number.isInteger(valor) || valor <= 0) {
    throw new ErrorDeConfiguracion(
      `\`${campo}\` en ${RUTA_CONFIGURACION} tiene que ser un entero mayor que cero. Llegó: ${JSON.stringify(valor)}.`,
    )
  }
  return valor
}

/**
 * ⭐⭐ `AW21` · Normaliza **una raíz HTTP**, que desde `ADR-0226` puede ser dos cosas:
 * una **URL absoluta** (los tres orígenes de siempre) o una **ruta de mismo origen**
 * (`/api/admin`), que es lo que hace posible que **una sola BasicAuth de Traefik
 * cierre `admin-web` y `bot-web` a la vez**.
 *
 * ⛔⛔ **`//host` NO ES UNA RUTA, Y ÉSA ES LA MITAD QUE NO SE VE LEYENDO.** La
 * comprobación ingenua —`valor.startsWith("/")`— la deja pasar, y el navegador
 * resuelve `//evil.example/x` como **`https://evil.example/x`**: es una URL
 * protocolo-relativa. ⇒ un `configuracion.json` mal escrito o manipulado
 * **mandaría la cookie de sesión del operador a un host ajeno**, con
 * `credentials: "include"` puesto por los diez bordes, y **nada fallaría**.
 * Por eso el criterio es `/` **seguido de algo que no sea `/`**.
 *
 * La barra final se saca en los dos casos, por lo mismo de siempre: para que armar
 * rutas más abajo sea concatenar sin pensar.
 *
 * ⚠️ **El mensaje de error NO cambia de forma para el caso viejo**: una instancia
 * que hoy falla por una URL mal escrita sigue leyendo lo mismo, más la mención de
 * que ahora también se admite una ruta.
 */
function raizNormalizada(valor: string, campo: string): string {
  // ⟦la-ruta-de-mismo-origen⟧ · `/` y después algo que NO sea otra `/`.
  if (/^\/[^/]/.test(valor)) return valor.replace(/\/+$/, "")

  try {
    return new URL(valor).href.replace(/\/+$/, "")
  } catch {
    throw new ErrorDeConfiguracion(
      `\`${campo}\` en ${RUTA_CONFIGURACION} no es una URL absoluta válida ni una ruta de mismo origen (\`/api/…\`). Llegó: ${JSON.stringify(valor)}.`,
    )
  }
}

/**
 * Valida la forma y normaliza la URL.
 *
 * Se le saca la barra final para que armar rutas más abajo sea concatenar sin
 * pensar: sin esto, `https://…/` + `/documentos` da `//documentos`, que muchos
 * servidores tratan distinto y ninguno avisa.
 */
export function interpretarConfiguracion(crudo: unknown): ConfiguracionInstancia {
  if (!esObjeto(crudo)) {
    throw new ErrorDeConfiguracion(`${RUTA_CONFIGURACION} tiene que ser un objeto JSON.`)
  }

  const { adminWebUrl } = crudo
  if (typeof adminWebUrl !== "string" || adminWebUrl.trim() === "") {
    throw new ErrorDeConfiguracion(
      `Falta \`adminWebUrl\` en ${RUTA_CONFIGURACION}. Es la dirección del servidor, y no tiene valor por defecto: la pantalla no puede adivinarla.`,
    )
  }

  /*
   * ⭐ `R2-UI6` · misma escalera que `adminWebUrl` y por el mismo motivo: sin la
   * raíz de `auth.<sitio>` no hay puerta, y no se puede adivinar. Ver el campo
   * en `ConfiguracionInstancia` para el radio que esto tiene fuera de `src/`.
   */
  const { identityWebUrl } = crudo
  if (typeof identityWebUrl !== "string" || identityWebUrl.trim() === "") {
    throw new ErrorDeConfiguracion(
      `Falta \`identityWebUrl\` en ${RUTA_CONFIGURACION}. Es la dirección de \`auth.<sitio>\` —por donde se entra y se cierra sesión—, y no tiene valor por defecto: la pantalla no puede adivinarla.`,
    )
  }

  return {
    adminWebUrl: raizNormalizada(adminWebUrl, "adminWebUrl"),
    identityWebUrl: raizNormalizada(identityWebUrl, "identityWebUrl"),
    refrescoMs: enteroPositivo(crudo["refrescoMs"], "refrescoMs", REFRESCO_MS_POR_DEFECTO),
    pageSize: enteroPositivo(crudo["pageSize"], "pageSize", PAGE_SIZE_POR_DEFECTO),
    ...brazoHibridoDeclarado(crudo["brazoHibrido"]),
    ...instanciaDeclarada(crudo["instancia"]),
    ...brazosDeclarados(crudo["brazos"]),
    ...derivacionPorBrazoDeclarada(crudo["derivacionPorBrazo"]),
    ...canalesDeclarados(crudo["canales"]),
    ...botonDeclarado(crudo["botonInicio"], "botonInicio"),
    ...botonDeclarado(crudo["botonDerivacion"], "botonDerivacion"),
    ...botonDeclarado(crudo["botonBaja"], "botonBaja"),
    ...botWebDeclarada(crudo["botWebUrl"]),
    ...esperaMaximaDeclarada(crudo["esperaMaximaDelSimuladorMs"]),
  }
}

/**
 * ⭐⭐ `AW59` · La espera máxima del simulador, **si la instancia la declaró**.
 *
 * **Misma forma que `botWebDeclarada`, y por las mismas dos razones:** se devuelve
 * un objeto para spread —con `exactOptionalPropertyTypes` prendido, un
 * `esperaMaximaDelSimuladorMs: undefined` explícito NO es lo mismo que la propiedad
 * ausente, y el tipo declara la segunda— y **un valor presente pero inservible se
 * RECHAZA en vez de degradarse a «ausente»**.
 *
 * ⛔ **Por qué se rechaza fuerte en vez de caer al ausente.** Los dos estados —«la
 * instancia no quiso el indicador» y «la instancia lo quiso y escribió mal el
 * número»— producen **la misma pantalla**: una sin indicador. Degradar dejaría al
 * operador creyendo que lo configuró, sin ningún síntoma. Es el mismo criterio que
 * `refrescoMs: 0` y `botWebUrl: ""` ya sostienen.
 *
 * ⛔ **Y no se usa `enteroPositivo`**, que es la función de al lado y sirve para lo
 * mismo: ésa toma un valor por defecto y lo aplica cuando falta, y **acá el ausente
 * NO tiene valor por defecto** — ver el argumento aritmético en la declaración del
 * campo. Pasarle uno sería escribir el default por la puerta de atrás.
 */
function esperaMaximaDeclarada(valor: unknown): { esperaMaximaDelSimuladorMs?: number } {
  if (valor === undefined || valor === null) return {}
  if (typeof valor !== "number" || !Number.isInteger(valor) || valor <= 0) {
    throw new ErrorDeConfiguracion(
      `\`esperaMaximaDelSimuladorMs\` en ${RUTA_CONFIGURACION} tiene que ser un entero de milisegundos mayor que cero —la espera máxima del simulador, fijada contra el tope del turno de esta instancia—, o no estar. Llegó: ${JSON.stringify(valor)}.`,
    )
  }
  return { esperaMaximaDelSimuladorMs: valor }
}

/**
 * ⭐ `AW16` · `botWebUrl`, si la instancia la declaró.
 *
 * **La misma forma que `brazoHibridoDeclarado`, y por las mismas dos razones:**
 * se devuelve un objeto para spread —con `exactOptionalPropertyTypes` prendido,
 * un `botWebUrl: undefined` explícito NO es lo mismo que la propiedad ausente, y
 * el tipo declara la segunda— y **un valor presente pero inservible se RECHAZA
 * en vez de degradarse a «ausente»**.
 *
 * ⛔ **Y acá esa segunda parte importa más que en `brazoHibrido`.** Degradar un
 * `""` o un `"bot-web"` sin esquema a «ausente» dejaría la pantalla del simulador
 * **sin montar** en una instancia que **cree que la configuró**: el operador no ve
 * un error, ve una entrada de menú que no está, que es indistinguible de la
 * ausencia deliberada que este campo existe para producir. Los dos estados tienen
 * que poder distinguirse, así que el que viene de un valor escrito mal **rompe el
 * arranque**, como `refrescoMs: 0`.
 *
 * La barra final se saca por lo mismo que en `adminWebUrl`: para que armar rutas
 * más abajo sea concatenar sin pensar.
 */
/**
 * ⭐ `AW20` · Uno de los tres identificadores de botón, si la instancia lo declaró.
 *
 * ⛔ **Ausente ⇒ la propiedad no se agrega**, y el botón no se dibuja. Se usa el
 * mismo truco que `brazoHibridoDeclarado` y `botWebDeclarada`: devolver `{}` en vez
 * de `{ boton: undefined }`, porque con `exactOptionalPropertyTypes` **no son lo
 * mismo** y sólo el primero deja la propiedad realmente ausente.
 *
 * ⚠️ **No se valida el CONTENIDO del identificador y no se puede.** Cuál es el id
 * correcto lo sabe la plantilla del brazo en el WhatsApp Manager, que no vive en
 * este repo. Un id mal cargado produce `interactivo-sin-ruteo`, que es **el motor
 * diciendo la verdad**, no un defecto de configuración que esta capa pueda atrapar.
 */
/**
 * ⭐⭐ `[2/9/2026]` · La lista de BRAZOS, si la instancia la declaró.
 *
 * **Copia deliberada de la forma de `canalesDeclarados`**, que está justo abajo:
 * mismo `{}` cuando falta (con `exactOptionalPropertyTypes`, `{}` y
 * `{ brazos: undefined }` **no son lo mismo**), mismo rechazo ruidoso de una
 * entrada vacía, mismo `trim`.
 *
 * ⛔ **Un brazo `""` rompe el arranque en vez de descartarse en silencio**, y acá
 * el argumento es más fuerte que en `canales`: un brazo vacío produce
 * `GET brazos//plantillas`, que **no es la ruta de ningún brazo** y contesta un
 * `404` que se lee como «el catálogo no existe» en vez de «la configuración está
 * mal». El síntoma no diría «configuración» — que es exactamente el motivo que
 * `canales` escribió para su propia falla cerrada.
 *
 * ⚠️ **Lo que esta función NO hace, y no puede:** verificar que los nombres sean
 * los brazos que la instancia declaró de verdad. `type Arm = string`; no hay
 * contra qué compararlos. Ver el hueco declarado en el campo de la interfaz.
 *
 * ⛔ **Tampoco de-duplica.** Un brazo repetido pediría el mismo catálogo dos
 * veces y lo mostraría dos veces. Se deja pasar **a propósito**: silenciarlo
 * escondería una configuración mal escrita, y rechazarlo sería inventar una regla
 * que ninguna fuente del repo fija. Queda visible en la pantalla, que es donde se
 * nota. ⚠️ **Y queda dicho como lo que es: una decisión, no un olvido.**
 */
function brazosDeclarados(valor: unknown): { brazos?: readonly string[] } {
  if (valor === undefined || valor === null) return {}
  if (!Array.isArray(valor)) {
    throw new ErrorDeConfiguracion(
      `\`brazos\` en ${RUTA_CONFIGURACION} tiene que ser una lista de nombres de brazo, o no estar.`,
    )
  }
  const limpios = valor.map((v, i) => {
    if (typeof v !== "string" || v.trim() === "") {
      throw new ErrorDeConfiguracion(
        `\`brazos[${i}]\` en ${RUTA_CONFIGURACION} no es un nombre de brazo: tiene que ser una cadena no vacía.`,
      )
    }
    return v.trim()
  })
  return limpios.length === 0 ? {} : { brazos: limpios }
}

/**
 * ⭐⭐ `ui-marco` · `[23/9/2026]` · La tabla de CAPACIDAD DE DERIVACIÓN por brazo,
 * si la instancia la declaró. Ver el campo en `ConfiguracionInstancia`, donde está
 * el argumento entero y los DOS huecos declarados.
 *
 * **Mismo molde que `brazosDeclarados` y `canalesDeclarados`**: `{}` cuando falta
 * (con `exactOptionalPropertyTypes`, `{}` y `{ derivacionPorBrazo: undefined }` no
 * son lo mismo), y rechazo RUIDOSO de una entrada mal escrita en vez de
 * descartarla en silencio.
 *
 * ⛔⛔ **Un valor que no sea `boolean` ROMPE EL ARRANQUE, y no se coerciona.** Un
 * `"false"` —la cadena— es `true` para cualquier `if`, así que coercionarlo
 * habilitaría la derivación en el brazo que la instancia quiso apagar, **sin que
 * nada falle**. Y descartarlo en silencio lo dejaría como ausente, que esta capa
 * trata como «no sé» — con lo cual la instancia que sí declaró el dato quedaría
 * indistinguible de la que no lo declaró. Las dos salidas cómodas pierden
 * información; romper la conserva.
 *
 * ⛔ **Una clave vacía también rompe.** `""` no es el nombre de ningún brazo, y
 * dejarla pasar produciría una entrada que nunca matchea: la pantalla se
 * comportaría como si el dato no estuviera, con el dato escrito a la vista.
 *
 * ⚠️ **Lo que esta función NO hace, y no puede:** verificar que las claves sean
 * los brazos que la instancia declaró de verdad, ni que respeten el `V4` de la
 * declaración de instancia (*«exactamente un brazo con derivación»*).
 * `type Arm = string`; no hay contra qué compararlas, y `V4` lo hace cumplir el
 * proceso que lee la declaración, no el navegador. Ver el hueco del campo.
 */
function derivacionPorBrazoDeclarada(
  valor: unknown,
): { derivacionPorBrazo?: Readonly<Record<string, boolean>> } {
  if (valor === undefined || valor === null) return {}
  if (!esObjeto(valor) || Array.isArray(valor)) {
    throw new ErrorDeConfiguracion(
      `\`derivacionPorBrazo\` en ${RUTA_CONFIGURACION} tiene que ser un objeto que asocie cada nombre de brazo con un booleano, o no estar.`,
    )
  }
  const limpio: Record<string, boolean> = {}
  for (const [brazo, habilitada] of Object.entries(valor)) {
    if (brazo.trim() === "") {
      throw new ErrorDeConfiguracion(
        `\`derivacionPorBrazo\` en ${RUTA_CONFIGURACION} tiene una clave vacía: las claves son nombres de brazo.`,
      )
    }
    if (typeof habilitada !== "boolean") {
      throw new ErrorDeConfiguracion(
        `\`derivacionPorBrazo["${brazo}"]\` en ${RUTA_CONFIGURACION} tiene que ser \`true\` o \`false\`. Llegó: ${JSON.stringify(habilitada)}.`,
      )
    }
    limpio[brazo.trim()] = habilitada
  }
  return Object.keys(limpio).length === 0 ? {} : { derivacionPorBrazo: limpio }
}

/**
 * ⭐⭐⭐ **¿ESTE BRAZO PUEDE DERIVAR A UNA PERSONA? — Y LA ÚNICA RESPUESTA HONESTA
 * CUANDO NO SE SABE ES «NO SÉ».**
 *
 * Devuelve `boolean | null`, y el `null` es la decisión entera. Quien llame tiene
 * que tratarlo, y eso es a propósito: **las dos formas de no tratarlo son defaults
 * disfrazados**, y `apps/admin-web/src/brazo-compartido.ts:265-285` ya los dejó
 * medidos del lado del servidor:
 *
 *   · `?? false` — silencia una opción **afirmando** que ese brazo no deriva. Es
 *     el más tentador porque «falla cerrado», y es el que esconde una capacidad
 *     real sin un síntoma.
 *   · `?? true` — ofrece registrar «pidió humano» en el Brazo Puro, que es
 *     exactamente lo que la regla 15 prohíbe. Allá se midió como
 *     `CP-R1-1-F1-053`: *«una cautela que se activa siempre no es una cautela: es
 *     el comportamiento»*.
 *
 * ⚠️⛔ **DIVERGENCIA DELIBERADA CON `brazo-compartido.ts`, y va dicha.** Allá la
 * función **lanza** y devuelve `boolean` a secas, con este argumento: *«si
 * admitiera “no sé” tendría que elegir una rama para ese caso, y ahí nace el
 * default otra vez»*. **Acá no se puede lanzar**: esto corre en el render de una
 * pantalla, y una excepción deja al operador con la consola en blanco por un dato
 * de configuración que la mitad de las pantallas ni usa. El molde de esta unidad
 * para ese caso ya está fijado por `botWebUrl`, `canales` y `enrolados`: **ausente ⟦rastro · A10
 * ⇒ el control no se dibuja, y no se inventa un valor.** Devolver `null` es lo que
 * le permite a la pantalla cumplirlo.
 *
 * ⛔ **Lo que este `null` NO impide, y queda dicho:** que quien lo reciba le
 * escriba un `?? false`. Para `tsc` eso compila. Ningún tipo puede impedirlo; lo
 * único que puede es que el caso «no declarado» tenga un examen propio en la
 * pantalla que lo consuma — y **ese examen es de `R2-UI8`**, no de acá.
 */
/**
 * ⭐ `R3-UI0` `[25/9/2026]` · **LO ÚNICO QUE `derivacionHabilitadaDe` NECESITA
 * LEER**, y por eso es su parámetro.
 *
 * Acá el parámetro era `ConfiguracionInstancia` entera. Se angosta para que
 * `zonas.ts` pueda llamar a esta función **sin conocer** la dirección de
 * `admin-web` ni el tamaño de página: ese archivo declara, en
 * §`DeclaracionDeZonas`, que su subconjunto de la configuración es *«un
 * subconjunto a propósito»*, y pasarle la configuración entera para leer un campo
 * habría sido derogar esa decisión de paso.
 *
 * ⛔ **Y NO se invierte la dependencia.** Este archivo **no** importa de
 * `zonas.ts`: la interfaz se declara acá, del lado de quien valida el campo. Al
 * revés habría un ciclo `configuracion → zonas → configuracion`.
 *
 * ⚠️ `ConfiguracionInstancia` sigue siendo asignable a esto —tiene el mismo campo
 * opcional con el mismo tipo—, así que los dos llamadores que ya existían
 * (`plantillas/botones/pantalla.tsx:324` y `pruebas/configuracion-derivacion.test.ts`)
 * no cambian una letra.
 */
export interface ConLaDerivacionDeclarada {
  readonly derivacionPorBrazo?: Readonly<Record<string, boolean>>
}

export function derivacionHabilitadaDe(
  configuracion: ConLaDerivacionDeclarada,
  brazo: string,
): boolean | null {
  const tabla = configuracion.derivacionPorBrazo
  if (tabla === undefined) return null
  const declarada = tabla[brazo]
  return typeof declarada === "boolean" ? declarada : null
}

/**
 * ⭐ `AW23` · La lista de canales, si la instancia la declaró.
 *
 * Ausente ⇒ la propiedad **no se agrega** (mismo truco que `botonDeclarado`: con
 * `exactOptionalPropertyTypes`, `{}` y `{ canales: undefined }` no son lo mismo).
 *
 * ⛔ **Una entrada vacía rompe el arranque en vez de descartarse en silencio.** Un
 * canal `""` en la lista dejaría el `<select>` con una opción que produce
 * `canal-no-ruteable`, y eso se lee como un defecto del motor. Es la misma falla
 * cerrada que `bot-web` §⟦los-origenes-fallan-cerrado⟧ eligió para su lista de CORS.
 */
function canalesDeclarados(valor: unknown): { canales?: readonly string[] } {
  if (valor === undefined || valor === null) return {}
  if (!Array.isArray(valor)) {
    throw new ErrorDeConfiguracion(
      `\`canales\` en ${RUTA_CONFIGURACION} tiene que ser una lista de nombres de canal receptor, o no estar.`,
    )
  }
  const limpios = valor.map((v, i) => {
    if (typeof v !== "string" || v.trim() === "") {
      throw new ErrorDeConfiguracion(
        `\`canales[${i}]\` en ${RUTA_CONFIGURACION} no es un nombre de canal: tiene que ser una cadena no vacía.`,
      )
    }
    return v.trim()
  })
  return limpios.length === 0 ? {} : { canales: limpios }
}

/**
 * La lista de ENROLADOS que declaraba la instancia se retiró con `A10`/`R3-UI4`.
 * Ver el ⟦rastro de abajo y la prosa de `R3-UI6`: acá no se lee ni se escribe.
 */
/* ⟦rastro · ADR-0082 · A10 · R3-UI4 — acá vivía `enroladosDeclarados(valor)`, el parser de la clave `enrolados` de `configuracion.json`. Se retiró ENTERO (el campo, el parser y el spread del objeto) porque la lista de participantes enrolados dejó de ser configuración de instancia: el selector del Simulador consulta `GET padron` (R3-UI6, A10/R3-E4). `interpretarConfiguracion` ignora las claves desconocidas, así que una `configuracion.json` vieja con esta clave arranca igual, sin degradarse a un estado inventado. */

function botonDeclarado(valor: unknown, campo: string): Record<string, string> {
  if (valor === undefined || valor === null) return {}
  if (typeof valor !== "string" || valor.trim() === "") {
    throw new ErrorDeConfiguracion(
      `\`${campo}\` en ${RUTA_CONFIGURACION} tiene que ser el identificador del botón tal como lo rutea el brazo, o no estar. Llegó: ${JSON.stringify(valor)}.`,
    )
  }
  return { [campo]: valor }
}

function botWebDeclarada(valor: unknown): { botWebUrl?: string } {
  if (valor === undefined || valor === null) return {}

  if (typeof valor !== "string" || valor.trim() === "") {
    throw new ErrorDeConfiguracion(
      `\`botWebUrl\` en ${RUTA_CONFIGURACION} tiene que ser la raíz HTTP de \`bot-web\`, o no estar. Llegó: ${JSON.stringify(valor)}.`,
    )
  }

  // `AW21` · misma regla que `adminWebUrl`, y por el mismo motivo: bajo la topología
  // de un solo origen (`ADR-0226`) esto vale `/api/bot`. Ver ⟦la-ruta-de-mismo-origen⟧.
  return { botWebUrl: raizNormalizada(valor, "botWebUrl") }
}

/**
 * `brazoHibrido`, si la instancia lo declaró.
 *
 * Se devuelve un objeto para spread y no un `string | undefined` porque
 * `exactOptionalPropertyTypes` está prendido: un `brazoHibrido: undefined`
 * explícito NO es lo mismo que la propiedad ausente, y el tipo declara la
 * segunda.
 *
 * Un `""` o un valor que no sea texto se RECHAZA en vez de tratarse como
 * ausente: los dos son configuración escrita a mano y mal, y degradarlos a
 * «ausente» dejaría la columna «Pidió humano» dibujada en el brazo puro con la
 * instancia creyendo que la configuró. Es el mismo criterio que ya rige para
 * `refrescoMs: 0`.
 */
function brazoHibridoDeclarado(valor: unknown): { brazoHibrido?: string } {
  if (valor === undefined || valor === null) return {}
  if (typeof valor !== "string" || valor.trim() === "") {
    throw new ErrorDeConfiguracion(
      `\`brazoHibrido\` en ${RUTA_CONFIGURACION} tiene que ser el nombre de uno de los brazos declarados por la instancia. Llegó: ${JSON.stringify(valor)}.`,
    )
  }
  return { brazoHibrido: valor }
}

/**
 * ⭐⭐ `[22/9/2026]` · El nombre de la instancia, **si lo declaró**.
 *
 * **Misma forma que `brazoHibridoDeclarado`, y por las mismas dos razones:** se
 * devuelve un objeto para spread —con `exactOptionalPropertyTypes` prendido, un
 * `instancia: undefined` explícito NO es lo mismo que la propiedad ausente, y el
 * tipo declara la segunda— y **un valor presente pero inservible se rechaza** en
 * vez de degradarse a ausente.
 *
 * ⛔ Que un `""` no sea «ausente» importa más acá que en ningún otro campo: un
 * nombre vacío dejaría todos los sellos como « · Brazo Puro», con un separador
 * suelto adelante, y **la instancia creyendo que se configuró**.
 */
function instanciaDeclarada(valor: unknown): { instancia?: string } {
  if (valor === undefined || valor === null) return {}
  if (typeof valor !== "string" || valor.trim() === "") {
    throw new ErrorDeConfiguracion(
      `\`instancia\` en ${RUTA_CONFIGURACION} tiene que ser el nombre visible de la instancia, o no estar. Llegó: ${JSON.stringify(valor)}.`,
    )
  }
  return { instancia: valor.trim() }
}

/**
 * Trae la configuración antes de montar la aplicación.
 *
 * `cache: "no-store"` porque este archivo es lo que cambia cuando la instancia
 * se reconfigura, y el resto del artefacto está pensado para cachearse fuerte
 * en CloudFront. Un `configuracion.json` cacheado dejaría a la pantalla hablando
 * con la dirección vieja después de una migración, sin ningún síntoma más que
 * datos que no llegan.
 *
 * Va adentro de un objeto exportado para que el módulo no exponga una función
 * `async` como named export: el gate de `AW10` descubre componentes montando
 * cada export de `src/**`, y una función async rompe ese descubrimiento antes de
 * llegar a la pantalla.
 */
export const configuracion = {
  async cargar(): Promise<ConfiguracionInstancia> {
    let respuesta: Response
    try {
      respuesta = await fetch(RUTA_CONFIGURACION, {
        cache: "no-store",
        headers: { Accept: "application/json" },
      })
    } catch (causa) {
      throw new ErrorDeConfiguracion(
        `No se pudo leer ${RUTA_CONFIGURACION}. Tiene que estar publicado al lado del \`index.html\`.`,
        { cause: causa },
      )
    }

    if (!respuesta.ok) {
      /**
       * ⚠️ **Éste no es un borde de `admin-web`, y aun así lee el cuerpo.**
       *
       * `configuracion.json` es un artefacto estático que sirve el mismo origen
       * que el `index.html`, así que un `403`/`404` de acá trae la página de
       * error de S3 o de CloudFront —XML o HTML, nunca `{error: …}`— y
       * `mensajeDeError` degrada al texto de siempre. Se lo llama igual por dos
       * razones: la rama queda uniforme con los otros catorce sitios de
       * `H-AUD-003`, y el día que la configuración la sirva algo que sí ponga un
       * motivo en el cuerpo, ese motivo ya se ve.
       */
      throw new ErrorDeConfiguracion(
        await mensajeDeError(
          respuesta,
          `${RUTA_CONFIGURACION} respondió ${respuesta.status}. Tiene que estar publicado al lado del \`index.html\`.`,
          /*
           * ⭐⭐ `R2-UI6` · **LA EXENCIÓN ② DE LA REACCIÓN CENTRAL, DECLARADA EN EL
           * MISMO COMMIT QUE LA REACCIÓN.** `// ADR-0353` §D1.
           *
           * `mensaje-de-error.ts` ahora traduce, para los trece bordes, un `401` a
           * `#/login` y un `403` a `AccesoDenegado`. **Esta llamada NO es un borde
           * de sesión**: pide `configuracion.json`, un artefacto ESTÁTICO servido
           * por S3/CloudFront desde el mismo origen que el `index.html`. Un `403`
           * de acá es un bucket mal permisionado o una distribución mal
           * configurada —`ADR-0217` es exactamente ese defecto medido— y **no es
           * un 403 de sesión**.
           *
           * ⛔ Sin esta bandera, un bucket mal permisionado mandaría al operador a
           * «No tienes acceso a esta pantalla», que es un diagnóstico FALSO y que
           * además esconde el único mensaje accionable que hay: el de acá abajo,
           * que nombra el archivo. Peor todavía: esto corre en `main.tsx` **antes
           * de que exista el `HashRouter`**, así que la navegación no tendría a
           * dónde ir.
           *
           * ⚠️ El cuerpo se sigue leyendo (ver el bloque de arriba): lo que se
           * exime es la REACCIÓN, no la lectura.
           */
          { noEsCodigoDeSesion: true },
        ),
      )
    }

    return interpretarConfiguracion(await respuesta.json())
  },
}
